'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, } from 'react';
import { mergeProps, useFocusRing, useHover } from 'react-aria';
export const ForceStateContext = createContext(null);
export function ForceState({ hover, active, focus, children, }) {
    return (_jsx(ForceStateContext.Provider, { value: { hover, active, focus }, children: children }));
}
/**
 * Wraps react-aria's hooks for pointer-aware hover and focus-visible.
 * Press state is tracked manually so callers can use the raw onClick
 * MouseEvent (e.g. to call `stopPropagation` when nested inside a parent link).
 */
export function useInteractive({ disabled, onClick, force, } = {}) {
    const ctx = useContext(ForceStateContext);
    const f = force ?? ctx ?? {};
    const forced = !!(force ?? ctx);
    const [pressed, setPressed] = useState(false);
    const { isHovered, hoverProps } = useHover({ isDisabled: !!disabled });
    const { isFocusVisible, focusProps } = useFocusRing();
    const releasePress = () => setPressed(false);
    const state = {
        hover: !!((f.hover ?? isHovered) && !disabled),
        active: !!((f.active ?? pressed) && !disabled),
        focus: !!((f.focus ?? isFocusVisible) && !disabled),
        focusForced: !!f.focus && forced && !disabled,
        disabled: !!disabled,
    };
    const bind = mergeProps(hoverProps, focusProps, {
        onMouseDown: () => {
            if (!disabled)
                setPressed(true);
        },
        onMouseUp: releasePress,
        onMouseLeave: releasePress,
        onClick: disabled ? undefined : onClick,
    });
    return { state, bind };
}
/**
 * Three-state background ladder shared by every interactive row/button:
 * pressed > hover > idle. Pass the row's at-rest fill as `idleBg`.
 */
export function interactiveBg(state, idleBg = 'transparent') {
    if (state.active)
        return 'var(--accent)';
    if (state.hover)
        return 'var(--background)';
    return idleBg;
}
//# sourceMappingURL=interactive.js.map